import { CancellableItemDto } from '@/services/cancellation.service';
import { ChoicesStatusMilestone } from '@/types';
import {
  Box,
  Button,
  Accordion,
  Typography,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { accordionSummaryClasses } from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { BilledMilestonesTable } from './billed-milestones-table';
import { CancellableMilestonesTable } from './cancellable-milestones-table';
import { isMilestoneDisabled } from '../../utils/cancel-order.utils';

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  flexDirection: 'row-reverse',
  gap: '6px',
  minHeight: '24px !important',
  height: '24px !important',
  '&.Mui-expanded': {
    minHeight: '24px !important',
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: 'rotate(90deg)',
  },
  [`& .${accordionSummaryClasses.content}`]: {
    margin: '0',
    alignItems: 'center',
  },
}));

interface CancelMilestoneItemProps {
  item: CancellableItemDto;
  selectedMilestones: string[];
  onMilestoneSelect: (milestoneId: string, selected: boolean) => void;
  onSelectAll: (allMilestoneIds: string[]) => void;
}

export const CancelMilestoneItem = ({
  item,
  selectedMilestones,
  onMilestoneSelect,
  onSelectAll,
}: CancelMilestoneItemProps) => {
  const { t } = useTranslation();

  // Handlers
  const handleSelectAll = () => {
    const enabledMilestoneIds = item.milestones
      .filter((milestone) => !isMilestoneDisabled(milestone.status))
      .map((milestone) => milestone._id);
    onSelectAll(enabledMilestoneIds);
  };

  const availableMilestones = item.milestones.filter(
    (milestone) => !isMilestoneDisabled(milestone.status)
  );

  // Check if all available milestones are selected
  const allSelected =
    availableMilestones.length > 0 &&
    availableMilestones.every((milestone) => selectedMilestones.includes(milestone._id));

  return (
    <Box
      key={item._id}
      borderRadius={'9px'}
      border={'1px solid'}
      borderColor={'divider'}
      px={'17px'}
      py={'14px'}
    >
      {/* Card Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography fontSize={'20px'} fontWeight={600} lineHeight={'30px'} color={'text.primary'}>
            {item.productName}
          </Typography>
          <Typography
            fontSize={'16px'}
            fontWeight={400}
            lineHeight={'24px'}
            color={'text.secondary'}
          >
            SKU: {item.sku} | Plan: {t(item.customerBillingFrequency)} | {t('Quantity')}:{' '}
            {item.quantity}
          </Typography>
        </Box>
        <Box>
          <Button
            onClick={handleSelectAll}
            disabled={availableMilestones.length === 0}
            sx={{
              minWidth: '147px',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: availableMilestones.length === 0 ? 'action.disabled' : 'divider',
              fontSize: '14px',
              fontWeight: '600',
              lineHeight: '22px',
              color: availableMilestones.length === 0 ? 'action.disabled' : 'text.primary',
              '&:hover': {
                borderColor: availableMilestones.length === 0 ? 'action.disabled' : 'primary.main',
              },
              '&:disabled': {
                color: 'action.disabled',
                borderColor: 'action.disabled',
              },
            }}
          >
            {availableMilestones.length === 0
              ? t('No available milestones')
              : allSelected
                ? t('Deselect All')
                : t('Select All')}
          </Button>
        </Box>
      </Box>

      {/* Card Body */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexDirection="column">
        {/* Billed Milesones */}
        <Box width="100%" mt="12px">
          <Accordion
            sx={{
              boxShadow: 'none !important',
              '&.Mui-expanded': {
                boxShadow: 'none !important',
              },
            }}
          >
            <StyledAccordionSummary
              expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography
                component="span"
                color="text.secondary"
                fontSize="15px"
                fontWeight={400}
                lineHeight="24px"
              >
                {t('Billed Milestones')} ({item.billedMilestones.length})
              </Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <BilledMilestonesTable milestones={item.billedMilestones} />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Cancellable Milesones */}
        <Box width="100%" mt="12px">
          <CancellableMilestonesTable
            milestones={item.milestones}
            selectedMilestones={selectedMilestones}
            onMilestoneSelect={onMilestoneSelect}
          />
        </Box>
      </Box>
    </Box>
  );
};
